import { translate } from '../../i18n/index.js';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import type React from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { PricingTier } from '@atomfit/shared';
import { useAuth } from '../../hooks/useAuth.js';
import { useMembershipCheckoutStore } from '../../store/membership-checkout.store.js';
import { LanguageSwitcher } from '../common/LanguageSwitcher.js';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export function CustomSelect({
  value,
  options,
  onChange,
  placeholder = translate('components.design.atom_fit_ui.custom_select.placeholder_select'),
  disabled = false,
  className,
}: {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  function choose(nextValue: string) {
    onChange(nextValue);
    setOpen(false);
  }

  function moveSelection(direction: 1 | -1) {
    const enabledOptions = options.filter((option) => !option.disabled);
    if (enabledOptions.length === 0) return;
    const currentIndex = enabledOptions.findIndex((option) => option.value === value);
    const nextIndex =
      currentIndex < 0
        ? direction === 1
          ? 0
          : enabledOptions.length - 1
        : (currentIndex + direction + enabledOptions.length) % enabledOptions.length;
    choose(enabledOptions[nextIndex]?.value ?? enabledOptions[0]?.value ?? '');
  }

  return (
    <div ref={rootRef} className={`af-custom-select${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className="af-select af-custom-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            return;
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) setOpen(true);
            else moveSelection(1);
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) setOpen(true);
            else moveSelection(-1);
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
      >
        <span className={selected ? undefined : 'af-custom-select-placeholder'}>
          {selected?.label ?? placeholder}
        </span>
      </button>
      {open && (
        <div id={`${id}-listbox`} className="af-custom-select-menu" role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`af-custom-select-option${option.value === value ? ' is-selected' : ''}`}
              disabled={option.disabled}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (!option.disabled) choose(option.value);
              }}
              onClick={() => choose(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function HexLogo({ size = 24, markClassName }: { size?: number; markClassName?: string }) {
  return (
    <svg
      className={markClassName}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="32" cy="33" r="26" stroke="var(--af-accent)" strokeWidth="1.45" />
      <circle cx="49" cy="15" r="6" fill="var(--af-cta)" />
      <text
        x="32"
        y="36"
        fill="var(--af-accent)"
        stroke="var(--af-accent)"
        strokeWidth="0.25"
        paintOrder="stroke fill"
        fontFamily="Manrope, system-ui, sans-serif"
        fontSize="30"
        fontWeight="900"
        letterSpacing="0"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {translate('components.design.atom_fit_ui.hex_logo.text.copy')}
      </text>
    </svg>
  );
}

export function useAtomFitDarkScheme() {
  const resolveIsDark = () =>
    typeof window !== 'undefined' &&
    getComputedStyle(document.documentElement).colorScheme.includes('dark');
  const [isDark, setIsDark] = useState(resolveIsDark);

  useEffect(() => {
    const update = () => setIsDark(resolveIsDark());
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const observer = new MutationObserver(update);

    update();
    media.addEventListener('change', update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
    return () => {
      media.removeEventListener('change', update);
      observer.disconnect();
    };
  }, []);

  return isDark;
}

export function AtomFitLogo({
  className,
  variant = 'auto',
}: {
  className?: string;
  variant?: 'auto' | 'dark';
}) {
  const isDark = useAtomFitDarkScheme();
  const useDarkLogo = variant === 'dark' || (variant === 'auto' && isDark);
  const src = useDarkLogo
    ? '/brand/atomfit-logo-olive-horizontal-dark.svg'
    : '/brand/atomfit-logo-olive-horizontal.svg';

  return (
    <img
      className={className}
      src={src}
      alt={translate('components.design.atom_fit_ui.atom_fit_logo.img.alt_brand_name')}
      draggable={false}
    />
  );
}

const landingNavItems = [
  {
    target: '#how-it-works',
    label: translate('components.design.atom_fit_ui.landing_nav_items.how_it_works'),
    icon: 'list',
  },
  {
    target: '#pricing',
    label: translate('components.design.atom_fit_ui.landing_nav_items.pricing'),
    icon: 'gift',
  },
  {
    target: '#faq',
    label: translate('components.design.atom_fit_ui.landing_nav_items.faq'),
    icon: 'help',
  },
  {
    target: '#contact',
    label: translate('components.design.atom_fit_ui.landing_nav_items.contact'),
    icon: 'user',
  },
] as const;

type LandingNavTarget = (typeof landingNavItems)[number]['target'];

export function AtomFitNav() {
  const { isAuthenticated, role, signOut } = useAuth();
  const { hash, pathname } = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement>(null);
  const scrollFrameRef = useRef(0);
  const hasMountedRef = useRef(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isRouteMorphing, setIsRouteMorphing] = useState(false);
  const [isDrawerMounted, setIsDrawerMounted] = useState(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [activeLandingSection, setActiveLandingSection] = useState<LandingNavTarget | null>(null);
  const resetMembershipCheckout = useMembershipCheckoutStore((state) => state.reset);
  const isDrawerOpen = isDrawerMounted && !isDrawerClosing;
  const isLandingPage = pathname === '/';
  const isProfileRoute =
    pathname === '/profile' ||
    pathname === '/reservations' ||
    pathname === '/membership' ||
    pathname === '/referrals' ||
    pathname === '/settings';
  const isNavExpanded = isLandingPage ? isScrolled : true;
  const accountLabel = isAuthenticated
    ? translate('components.design.atom_fit_ui.atom_fit_nav.account_label')
    : translate('components.design.atom_fit_ui.atom_fit_nav.account_label_2');
  const accountTarget = isAuthenticated ? '/reservations' : '/login';
  const canOpenAdmin = role === 'PLATFORM_ADMIN' || role === 'OWNER_ADMIN' || role === 'GYM_STAFF';

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    setIsRouteMorphing(true);
    const timeout = window.setTimeout(() => setIsRouteMorphing(false), 1500);
    return () => window.clearTimeout(timeout);
  }, [pathname]);

  useEffect(() => {
    if (!isLandingPage) {
      setActiveLandingSection(null);
      let frame = 0;

      function syncExpandedNav() {
        const nav = navRef.current;
        if (!nav) {
          setIsScrolled(true);
          return;
        }

        const viewportWidth = document.documentElement.clientWidth;
        if (viewportWidth <= 760) {
          nav.removeAttribute('style');
          setIsScrolled(true);
          return;
        }

        nav.style.setProperty('--af-nav-top', '0px');
        nav.style.setProperty('--af-nav-width', `${viewportWidth}px`);
        nav.style.setProperty('--af-nav-radius', '0px');
        nav.style.setProperty('--af-nav-outline-opacity', '0');
        nav.style.setProperty('--af-nav-shadow-opacity', '0.1');
        setIsScrolled(true);
      }

      frame = window.requestAnimationFrame(syncExpandedNav);
      window.addEventListener('resize', syncExpandedNav);
      return () => {
        if (frame) window.cancelAnimationFrame(frame);
        window.removeEventListener('resize', syncExpandedNav);
      };
    }

    let frame = 0;

    function syncScrollState() {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const nav = navRef.current;
        const hero = document.querySelector<HTMLElement>('.af-landing-hero');
        if (!nav || !hero) return;

        const viewportWidth = document.documentElement.clientWidth;
        const isMobile = viewportWidth <= 760;
        if (isMobile) {
          nav.removeAttribute('style');
          setIsScrolled(false);
          return;
        }

        const floatingWidth = Math.min(1180, viewportWidth - 48);
        const fullWidth = viewportWidth;
        const morphDistance = Math.max(1, hero.offsetHeight - 80);
        const progress = Math.min(1, Math.max(0, window.scrollY / morphDistance));

        const floatingTop = 21;
        nav.style.setProperty('--af-nav-top', `${floatingTop * (1 - progress)}px`);
        nav.style.setProperty(
          '--af-nav-width',
          `${floatingWidth + (fullWidth - floatingWidth) * progress}px`,
        );
        nav.style.setProperty('--af-nav-radius', `${8 * (1 - progress)}px`);
        nav.style.setProperty('--af-nav-outline-opacity', String(0.26 * (1 - progress)));
        nav.style.setProperty('--af-nav-shadow-opacity', String(0.18 - 0.08 * progress));
        setIsScrolled(progress >= 1);
      });
    }

    function syncScrollStateImmediately() {
      const hero = document.querySelector<HTMLElement>('.af-landing-hero');
      setIsScrolled(hero ? hero.getBoundingClientRect().bottom <= 80 : false);
      syncScrollState();
    }

    syncScrollStateImmediately();
    window.addEventListener('scroll', syncScrollState, { passive: true });
    window.addEventListener('resize', syncScrollState);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', syncScrollState);
      window.removeEventListener('resize', syncScrollState);
    };
  }, [isLandingPage]);

  useEffect(() => {
    if (!isLandingPage) return;

    let frame = 0;

    function syncActiveSection() {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const anchorY = 96;
        const isAtPageBottom =
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
        if (isAtPageBottom) {
          setActiveLandingSection('#contact');
          return;
        }

        const activeItem = landingNavItems.find((item) => {
          const section = document.querySelector<HTMLElement>(item.target);
          if (!section) return false;

          const rect = section.getBoundingClientRect();
          return rect.top <= anchorY && rect.bottom > anchorY;
        });

        setActiveLandingSection(activeItem?.target ?? null);
      });
    }

    syncActiveSection();
    window.addEventListener('scroll', syncActiveSection, { passive: true });
    window.addEventListener('resize', syncActiveSection);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', syncActiveSection);
      window.removeEventListener('resize', syncActiveSection);
    };
  }, [isLandingPage]);

  useEffect(() => {
    setIsDrawerMounted(false);
    setIsDrawerClosing(false);
  }, [pathname]);

  useEffect(() => {
    return () => {
      if (scrollFrameRef.current) window.cancelAnimationFrame(scrollFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isLandingPage || !hash) return;
    const matchingItem = landingNavItems.find((item) => item.target === hash);
    setActiveLandingSection(matchingItem?.target ?? null);

    const frame = window.requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(hash);
      if (!target) return;

      const navOffset = 84;
      const targetY = Math.max(0, window.scrollY + target.getBoundingClientRect().top - navOffset);
      animateScrollTo(targetY);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [hash, isLandingPage]);

  useEffect(() => {
    if (!isDrawerOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') closeDrawer();
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDrawerOpen, isDrawerClosing]);

  function handleLogoClick(event: React.MouseEvent<HTMLAnchorElement>) {
    closeDrawer();
    if (!isLandingPage) return;
    event.preventDefault();
    window.history.replaceState(null, '', '/');
    animateScrollTo(0);
  }

  function animateScrollTo(targetY: number, duration = 1200, finalHash?: string) {
    if (scrollFrameRef.current) window.cancelAnimationFrame(scrollFrameRef.current);

    const startY = window.scrollY;
    const distance = targetY - startY;
    const start = window.performance.now();
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    function easeInOutCubic(value: number) {
      return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
    }

    function step(now: number) {
      const progress = Math.min(1, (now - start) / duration);
      window.scrollTo({ top: startY + distance * easeInOutCubic(progress), behavior: 'auto' });
      if (progress < 1) {
        scrollFrameRef.current = window.requestAnimationFrame(step);
        return;
      }

      scrollFrameRef.current = 0;
      root.style.scrollBehavior = previousScrollBehavior;
      if (finalHash) window.history.pushState(null, '', finalHash);
    }

    scrollFrameRef.current = window.requestAnimationFrame(step);
  }

  function handleSectionNavClick(targetId: string) {
    closeDrawer();
    if (!isLandingPage) {
      navigate({ pathname: '/', hash: targetId });
      return;
    }

    const target = document.querySelector<HTMLElement>(targetId);
    if (!target) return;

    const navOffset = 84;
    const targetY = Math.max(0, window.scrollY + target.getBoundingClientRect().top - navOffset);
    setActiveLandingSection(targetId as LandingNavTarget);
    animateScrollTo(targetY, 1200, targetId);
  }

  function openDrawer() {
    setIsDrawerClosing(false);
    setIsDrawerMounted(true);
  }

  function closeDrawer() {
    if (!isDrawerMounted || isDrawerClosing) return;
    setIsDrawerClosing(true);
  }

  function requestSignOut() {
    closeDrawer();
    setIsLogoutConfirmOpen(true);
  }

  function startReservationFlow() {
    resetMembershipCheckout();
    closeDrawer();
  }

  async function handleSignOut() {
    setIsLogoutConfirmOpen(false);
    setIsDrawerMounted(false);
    setIsDrawerClosing(false);
    await signOut();
    void navigate('/', { replace: true });
  }

  return (
    <>
      <nav
        ref={navRef}
        className={`af-nav${isLandingPage ? ' is-landing' : ''}${isNavExpanded ? ' is-scrolled' : ''}${isRouteMorphing ? ' is-route-morphing' : ''}${isProfileRoute ? ' is-profile' : ''}`}
      >
        <div className="af-nav-inner">
          <Link className="af-logo" to="/" onClick={handleLogoClick}>
            <AtomFitLogo />
          </Link>
          <div
            className="af-nav-center"
            aria-label={translate(
              'components.design.atom_fit_ui.atom_fit_nav.div_nav_center.aria_label_content',
            )}
          >
            {isProfileRoute ? (
              <>
                <NavLink
                  className={({ isActive }) =>
                    `af-nav-link af-profile-nav-link${isActive ? ' is-active' : ''}`
                  }
                  to="/reservations"
                >
                  {translate('components.design.atom_fit_ui.atom_fit_nav.nav_link.booking')}
                </NavLink>
                <NavLink
                  className={({ isActive }) =>
                    `af-nav-link af-profile-nav-link${isActive ? ' is-active' : ''}`
                  }
                  to="/membership"
                >
                  {translate('components.design.atom_fit_ui.atom_fit_nav.nav_link.membership')}
                </NavLink>
                <NavLink
                  className={({ isActive }) =>
                    `af-nav-link af-profile-nav-link${isActive ? ' is-active' : ''}`
                  }
                  to="/settings"
                >
                  {translate('components.design.atom_fit_ui.atom_fit_nav.nav_link.settings')}
                </NavLink>
                {canOpenAdmin && (
                  <NavLink
                    className={({ isActive }) =>
                      `af-nav-link af-profile-nav-link${isActive ? ' is-active' : ''}`
                    }
                    to="/admin"
                  >
                    {translate('components.design.atom_fit_ui.atom_fit_nav.nav_link.admin')}
                  </NavLink>
                )}
              </>
            ) : (
              <>
                {landingNavItems.map((item) => (
                  <button
                    className={`af-nav-link af-section-nav-link${activeLandingSection === item.target ? ' is-active' : ''}`}
                    type="button"
                    key={item.target}
                    onClick={() => handleSectionNavClick(item.target)}
                  >
                    {item.label}
                  </button>
                ))}
              </>
            )}
          </div>
          <div className="af-nav-actions">
            <LanguageSwitcher />
            {isProfileRoute ? (
              <>
                <button
                  className="af-nav-link af-profile-nav-link af-profile-nav-logout"
                  type="button"
                  onClick={requestSignOut}
                >
                  {translate(
                    'components.design.atom_fit_ui.atom_fit_nav.button_nav_link_profile_nav_link.logout',
                  )}
                </button>
                <Link
                  className="af-nav-reserve af-primary-btn"
                  to="/gyms"
                  onClick={startReservationFlow}
                >
                  {translate(
                    'components.design.atom_fit_ui.atom_fit_nav.link_nav_reserve_primary_btn.book',
                  )}
                </Link>
              </>
            ) : (
              <>
                <NavLink className="af-nav-link" to={accountTarget}>
                  {accountLabel}
                </NavLink>
                <Link
                  className="af-nav-reserve af-primary-btn"
                  to="/gyms"
                  onClick={startReservationFlow}
                >
                  {translate(
                    'components.design.atom_fit_ui.atom_fit_nav.link_nav_reserve_primary_btn.book_2',
                  )}
                </Link>
              </>
            )}
          </div>
          <button
            className="af-nav-menu-btn"
            type="button"
            aria-label={translate(
              'components.design.atom_fit_ui.atom_fit_nav.button_nav_menu_btn.aria_label_open',
            )}
            aria-expanded={isDrawerOpen}
            onClick={openDrawer}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>
      {isDrawerMounted ? (
        <div
          className={`af-nav-drawer-shell${isDrawerClosing ? ' is-closing' : ''}`}
          role="presentation"
          aria-hidden={isDrawerClosing}
        >
          <button
            className="af-nav-drawer-backdrop"
            type="button"
            aria-label={translate(
              'components.design.atom_fit_ui.atom_fit_nav.button_nav_drawer_backdrop.aria_label_close',
            )}
            onClick={closeDrawer}
          />

          <aside
            className="af-nav-drawer"
            aria-label={translate(
              'components.design.atom_fit_ui.atom_fit_nav.aside_nav_drawer.aria_label_content',
            )}
          >
            <div className="af-nav-drawer-head">
              <Link className="af-logo" to="/" onClick={handleLogoClick}>
                <AtomFitLogo />
              </Link>
              <button
                className="af-nav-drawer-close"
                type="button"
                aria-label={translate(
                  'components.design.atom_fit_ui.atom_fit_nav.button_nav_drawer_close.aria_label_close',
                )}
                onClick={closeDrawer}
              >
                {'×'}
              </button>
            </div>
            <div className="af-nav-drawer-items">
              {isProfileRoute ? (
                <>
                  <NavLink
                    className="af-nav-dropdown-item"
                    to="/reservations"
                    onClick={closeDrawer}
                  >
                    <DrawerIcon name="calendar" />
                    {translate(
                      'components.design.atom_fit_ui.atom_fit_nav.nav_link_nav_dropdown_item.booking',
                    )}
                  </NavLink>
                  <NavLink className="af-nav-dropdown-item" to="/membership" onClick={closeDrawer}>
                    <DrawerIcon name="gift" />
                    {translate(
                      'components.design.atom_fit_ui.atom_fit_nav.nav_link_nav_dropdown_item.membership',
                    )}
                  </NavLink>
                  <NavLink className="af-nav-dropdown-item" to="/settings" onClick={closeDrawer}>
                    <DrawerIcon name="settings" />
                    {translate(
                      'components.design.atom_fit_ui.atom_fit_nav.nav_link_nav_dropdown_item.settings',
                    )}
                  </NavLink>
                  {canOpenAdmin && (
                    <NavLink className="af-nav-dropdown-item" to="/admin" onClick={closeDrawer}>
                      <DrawerIcon name="shield" />
                      {translate(
                        'components.design.atom_fit_ui.atom_fit_nav.nav_link_nav_dropdown_item.admin',
                      )}
                    </NavLink>
                  )}
                  <button
                    className="af-nav-dropdown-item af-nav-dropdown-item--danger"
                    type="button"
                    onClick={requestSignOut}
                  >
                    <DrawerIcon name="logout" />
                    {translate(
                      'components.design.atom_fit_ui.atom_fit_nav.button_nav_dropdown_item_nav_dropdown_item_danger.logout',
                    )}
                  </button>
                  <Link
                    className="af-nav-dropdown-item af-nav-dropdown-item--primary"
                    to="/gyms"
                    onClick={startReservationFlow}
                  >
                    <DrawerIcon name="calendar" />
                    {translate(
                      'components.design.atom_fit_ui.atom_fit_nav.link_nav_dropdown_item_nav_dropdown_item_primary.book',
                    )}
                  </Link>
                </>
              ) : (
                <>
                  {landingNavItems.map((item) => (
                    <button
                      className={`af-nav-dropdown-item${activeLandingSection === item.target ? ' is-active' : ''}`}
                      type="button"
                      key={item.target}
                      onClick={() => handleSectionNavClick(item.target)}
                    >
                      <DrawerIcon name={item.icon} />
                      {item.label}
                    </button>
                  ))}
                  <NavLink
                    className="af-nav-dropdown-item"
                    to={accountTarget}
                    onClick={closeDrawer}
                  >
                    <DrawerIcon name="user" />
                    {accountLabel}
                  </NavLink>
                  <Link
                    className="af-nav-dropdown-item af-nav-dropdown-item--primary"
                    to="/gyms"
                    onClick={startReservationFlow}
                  >
                    <DrawerIcon name="calendar" />
                    {translate(
                      'components.design.atom_fit_ui.atom_fit_nav.link_nav_dropdown_item_nav_dropdown_item_primary.book_2',
                    )}
                  </Link>
                </>
              )}
            </div>
            <div className="af-nav-drawer-language">
              <LanguageSwitcher />
            </div>
          </aside>
        </div>
      ) : null}
      {isLogoutConfirmOpen && (
        <div
          className="af-profile-modal-backdrop"
          role="presentation"
          onClick={() => setIsLogoutConfirmOpen(false)}
        >
          <div
            className="af-profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="af-logout-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="af-logout-title" style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}>
              {translate('components.design.atom_fit_ui.atom_fit_nav.h2.logout_confirmation')}
            </h2>
            <p
              style={{
                margin: '0 0 22px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {translate('components.design.atom_fit_ui.atom_fit_nav.p.login')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="af-outline-btn"
                type="button"
                style={{ padding: '10px 16px' }}
                onClick={() => setIsLogoutConfirmOpen(false)}
              >
                {translate('components.design.atom_fit_ui.atom_fit_nav.button_outline_btn.cancel')}
              </button>
              <button
                className="af-primary-btn"
                type="button"
                style={{ padding: '10px 18px' }}
                onClick={() => void handleSignOut()}
              >
                {translate('components.design.atom_fit_ui.atom_fit_nav.button_primary_btn.logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DrawerIcon({
  name,
  className = 'af-nav-drawer-icon',
}: {
  name:
    | 'calendar'
    | 'list'
    | 'help'
    | 'building'
    | 'user'
    | 'gift'
    | 'settings'
    | 'shield'
    | 'logout';
  className?: string;
}) {
  const paths: Record<typeof name, React.ReactNode> = {
    calendar: (
      <>
        <path d="M5 6.5h14M8 4v5M16 4v5" />
        <rect x="5" y="5.5" width="14" height="14" rx="2.5" />
        <path d="M8.5 12h2M13.5 12h2M8.5 15.5h2M13.5 15.5h2" />
      </>
    ),

    list: (
      <>
        <path d="M9 7h10M9 12h10M9 17h10" />
        <path d="M5 7h.01M5 12h.01M5 17h.01" />
      </>
    ),

    help: (
      <>
        <circle cx="12" cy="12" r="7.5" />
        <path d="M9.8 9.6a2.4 2.4 0 0 1 4.6 1c0 1.7-1.7 2-2.2 3.1" />
        <path d="M12 16.8h.01" />
      </>
    ),

    building: (
      <>
        <path d="M5.5 19V6.5A1.5 1.5 0 0 1 7 5h10a1.5 1.5 0 0 1 1.5 1.5V19" />
        <path d="M4 19h16M9 8.5h1.4M13.6 8.5H15M9 12h1.4M13.6 12H15M9 15.5h1.4M13.6 15.5H15" />
      </>
    ),

    user: (
      <>
        <circle cx="12" cy="8" r="3" />
        <path d="M5.5 19c.8-3.2 3.1-5 6.5-5s5.7 1.8 6.5 5" />
      </>
    ),

    gift: (
      <>
        <path d="M4.5 10h15v9h-15zM4 10h16V7H4zM12 7v12" />
        <path d="M12 7c-1.8 0-4.2-.5-4.2-2.1C7.8 3.9 8.6 3 9.7 3 11.2 3 12 5 12 7zM12 7c1.8 0 4.2-.5 4.2-2.1 0-1-.8-1.9-1.9-1.9C12.8 3 12 5 12 7z" />
      </>
    ),

    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 4.5v2M12 17.5v2M5.5 8l1.8 1M16.7 15l1.8 1M5.5 16l1.8-1M16.7 9l1.8-1" />
      </>
    ),

    shield: (
      <>
        <path d="M12 4.5 18.5 7v5.2c0 4-2.7 6.2-6.5 7.3-3.8-1.1-6.5-3.3-6.5-7.3V7L12 4.5z" />
        <path d="m9.5 12 1.7 1.7 3.5-3.7" />
      </>
    ),

    logout: (
      <>
        <path d="M10 6H6.5A1.5 1.5 0 0 0 5 7.5v9A1.5 1.5 0 0 0 6.5 18H10" />
        <path d="M14 8l4 4-4 4M18 12H9" />
      </>
    ),
  };

  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        {paths[name]}
      </g>
    </svg>
  );
}

export function Tag({
  children,
  color = 'var(--af-accent)',
  bg = 'var(--af-accent-soft)',
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
}) {
  return (
    <span
      className="af-tag"
      style={{ '--tag-color': color, '--tag-bg': bg } as React.CSSProperties}
    >
      {children}
    </span>
  );
}

export function TierPill({ tier }: { tier: PricingTier }) {
  const map: Record<PricingTier, { color: string; bg: string; label: string }> = {
    PEAK: {
      color: 'var(--af-peak)',
      bg: 'var(--af-peak-soft)',
      label: translate('components.design.atom_fit_ui.map.label'),
    },
    STANDARD: {
      color: 'var(--af-muted)',
      bg: 'rgba(255,255,255,0.06)',
      label: translate('components.design.atom_fit_ui.map.label_2'),
    },
    OFF_PEAK: {
      color: 'var(--af-dim)',
      bg: 'rgba(255,255,255,0.04)',
      label: translate('components.design.atom_fit_ui.map.label_3'),
    },
    LATE_NIGHT: {
      color: 'var(--af-accent)',
      bg: 'var(--af-accent-soft)',
      label: translate('components.design.atom_fit_ui.map.label_4'),
    },
  };
  const item = map[tier];
  return (
    <Tag color={item.color} bg={item.bg}>
      {item.label}
    </Tag>
  );
}

export function BackLink({
  to,
  children = translate('components.design.atom_fit_ui.back_link.children'),
}: {
  to: string;
  children?: React.ReactNode;
}) {
  return (
    <Link className="af-back" to={to}>
      <ArrowLeftIcon />
      {children}
    </Link>
  );
}

export function Spinner() {
  return <span className="af-spinner" aria-hidden="true" />;
}

export function formatCurrency(amount: number, currency = 'CZK') {
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

export function formatTime(value: Date | string) {
  return new Date(value).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
}

export function QRCode({ size = 118 }: { size?: number }) {
  const n = 21;
  const c = size / n;
  const cells: Array<[number, number]> = [];
  const corner = (ox: number, oy: number) => {
    for (let row = 0; row < 7; row += 1) {
      for (let col = 0; col < 7; col += 1) {
        const filled =
          row === 0 ||
          row === 6 ||
          col === 0 ||
          col === 6 ||
          (row >= 2 && row <= 4 && col >= 2 && col <= 4);
        if (filled) cells.push([ox + col, oy + row]);
      }
    }
  };

  corner(0, 0);
  corner(14, 0);
  corner(0, 14);
  for (let row = 0; row < n; row += 1) {
    for (let col = 0; col < n; col += 1) {
      if ((row < 8 && col < 8) || (row < 8 && col >= 13) || (row >= 13 && col < 8)) continue;
      if (((row * 3 + col * 7 + row) ^ col) % 3 === 0) cells.push([col, row]);
    }
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <rect width={size} height={size} fill="#fff" />
      {cells.map(([x, y], index) => (
        <rect key={`${x}-${y}-${index}`} x={x * c} y={y * c} width={c} height={c} fill="#000" />
      ))}
    </svg>
  );
}

export function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M10 3L5 8l5 5M5.5 8H14"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LockIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect
        x="5"
        y="13"
        width="18"
        height="12"
        rx="3"
        stroke="var(--af-accent)"
        strokeWidth="1.8"
      />

      <path
        d="M9 13V9a5 5 0 0 1 10 0v4"
        stroke="var(--af-accent)"
        strokeWidth="1.8"
        strokeLinecap="round"
      />

      <circle cx="14" cy="19" r="2" fill="var(--af-accent)" />
    </svg>
  );
}

export function BoltIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path
        d="M16 3L7 16h8l-3 9 10-13h-8l3-9z"
        stroke="var(--af-accent)"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BuildingIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect x="4" y="8" width="20" height="17" rx="2" stroke="var(--af-accent)" strokeWidth="1.8" />
      <path d="M10 25V17h8v8" stroke="var(--af-accent)" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M4 12h20" stroke="var(--af-accent)" strokeWidth="1.4" opacity="0.5" />
      <rect x="9" y="10" width="3" height="3" rx="0.5" fill="var(--af-accent)" opacity="0.5" />
      <rect x="16" y="10" width="3" height="3" rx="0.5" fill="var(--af-accent)" opacity="0.5" />
      <path d="M10 3l4-2 4 2" stroke="var(--af-accent)" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M11.5 11.5L16 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path
        d="M5 14l7 7L23 7"
        stroke="var(--af-accent)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
